import React, { useState } from "react";
import {
  IconTrash,
  IconPlus,
  IconUpload,
  IconMinus,
} from "@tabler/icons-react";
import {
  ProductFormState,
  VariantForm,
  StorageItem,
} from "../../../types/types";
import { useFetchCountriesAllData } from "../../../apis/locationApis";
import {
  useProductCategoryAllListData,
  useProductSubCategoriesListData,
} from "../../../apis/productApis";
import { MediaUploader } from "../../InventoryManagement/add-item";
import FormErrorMessage from "../../../components/common/FormErrorMessage";
import { useLanguage } from "../../../context/LanguageContext";
import Input from "../../../components/Input";
import SelectInput from "../../../components/Select";
import ButtonStyled from "../../../components/common/ButtonStyled";
import TextareaField from "../../../components/common/TextareaField";

interface CompilerProps {
  productForm: ProductFormState;
  formVariants: VariantForm[];
  imagePreview: string;
  financialMetrics: {
    finalPrice: number;
    estimatedDuty: string;
    estimatedVat: string;
    landedCost: string;
    grossTax: string;
  };
  onProductFormChange: (updates: Partial<ProductFormState>) => void;
  onVariantsChange: (variants: VariantForm[]) => void;
  onImagePreviewChange: (url: string) => void;
  onLoadDemo: () => void;
  onDiscard: () => void;
  onSubmit: (e: React.FormEvent) => void;
  categories?: any[];
  isSubmitting?: boolean;
  editingProductId?: string;
  onSuccess?: () => void;
  errors?: Record<string, { message: string }>;
  hideHeaderButtons?: boolean;
}

const STORAGE_TYPES = [
  { id: 1, value: "PALLETS", text: "PALLETS" },
  { id: 2, value: "BINS", text: "BINS" },
];

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3",
}: {
  icon?: any;
  title: string;
  className?: string;
}) => {
  const { t } = useLanguage();
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

export const Compiler: React.FC<CompilerProps> = ({
  productForm,
  formVariants,
  imagePreview,
  isSubmitting,
  onProductFormChange,
  onVariantsChange,
  onImagePreviewChange,
  onLoadDemo,
  onDiscard,
  onSubmit,
  categories = [],
  errors = {},
  editingProductId,
  hideHeaderButtons = false,
}) => {
  const { data: countriesData } = useFetchCountriesAllData();
  const { data: fetchedCategories } = useProductCategoryAllListData();
  const allCategories =
    categories && categories.length > 0 ? categories : fetchedCategories;

  const selectedCategoryObj = React.useMemo(() => {
    if (!Array.isArray(allCategories) || !productForm.category) return null;
    return allCategories.find(
      (c: any) =>
        c.id === productForm.category ||
        c.category === productForm.category ||
        c.categoryName === productForm.category,
    );
  }, [allCategories, productForm.category]);

  const catParamById = selectedCategoryObj?.id || productForm.category || "";
  const catParamByName =
    selectedCategoryObj?.category ||
    selectedCategoryObj?.categoryName ||
    productForm.category ||
    "";

  const { data: subCategoriesById } =
    useProductSubCategoriesListData(catParamById);
  const { data: subCategoriesByName } =
    useProductSubCategoriesListData(catParamByName);

  const subCategoriesData = React.useMemo(() => {
    if (Array.isArray(subCategoriesById) && subCategoriesById.length > 0) {
      return subCategoriesById;
    }
    if (Array.isArray(subCategoriesByName) && subCategoriesByName.length > 0) {
      return subCategoriesByName;
    }
    return [];
  }, [subCategoriesById, subCategoriesByName]);

  const selectedCategoryValue = React.useMemo(() => {
    if (!productForm.category || !Array.isArray(allCategories))
      return productForm.category || "";
    const matched = allCategories.find(
      (c: any) =>
        c.id === productForm.category ||
        c.category === productForm.category ||
        c.categoryName === productForm.category,
    );
    return matched ? matched.id || matched.category : productForm.category;
  }, [allCategories, productForm.category]);

  const selectedSubCategoryValue = React.useMemo(() => {
    if (!productForm.subcategory || !Array.isArray(subCategoriesData))
      return productForm.subcategory || "";
    const matched = subCategoriesData.find(
      (sc: any) =>
        sc.id === productForm.subcategory ||
        sc.subCategory === productForm.subcategory ||
        sc.subCategoryName === productForm.subcategory ||
        sc.name === productForm.subcategory,
    );
    return matched
      ? matched.id || matched.subCategory || matched.name
      : productForm.subcategory;
  }, [subCategoriesData, productForm.subcategory]);

  const selectedSubCategoryObj = React.useMemo(() => {
    if (!productForm.subcategory || !Array.isArray(subCategoriesData))
      return null;
    return subCategoriesData.find(
      (sc: any) =>
        sc.id === productForm.subcategory ||
        sc.subCategory === productForm.subcategory ||
        sc.subCategoryName === productForm.subcategory ||
        sc.name === productForm.subcategory,
    );
  }, [subCategoriesData, productForm.subcategory]);

  const subCategoryFields = React.useMemo(() => {
    return selectedSubCategoryObj?.fields || [];
  }, [selectedSubCategoryObj]);

  const uniqueCategories = React.useMemo(() => {
    if (!Array.isArray(allCategories)) return [];
    const seen = new Set();
    return allCategories.filter((c: any) => {
      const name = (c.categoryName || c.category || c.name || "")
        .trim()
        .toLowerCase();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  }, [allCategories]);

  const uniqueSubCategories = React.useMemo(() => {
    if (!Array.isArray(subCategoriesData)) return [];
    const seen = new Set();
    return subCategoriesData.filter((sc: any) => {
      const name = (sc.subCategory || sc.subCategoryName || sc.name || "")
        .trim()
        .toLowerCase();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  }, [subCategoriesData]);

  const getFormattedImgUrl = (url?: any) => {
    if (!url) return "";
    if (url instanceof File || url instanceof Blob) {
      return URL.createObjectURL(url);
    }
    const str = String(url);
    if (
      str.startsWith("http://") ||
      str.startsWith("https://") ||
      str.startsWith("blob:") ||
      str.startsWith("data:")
    ) {
      return str;
    }
    const baseUrl = process.env.REACT_APP_BASE_URL || "";
    const cleanPath = str.startsWith("/") ? str : `/${str}`;
    return `${baseUrl}${cleanPath}`;
  };

  const [media, setMedia] = useState<any[]>(() => {
    if (productForm.mediaList && productForm.mediaList.length > 0) {
      return productForm.mediaList;
    }
    if (imagePreview) {
      return [{ file: imagePreview, preview: imagePreview, type: "image" }];
    }
    return [];
  });

  React.useEffect(() => {
    if (productForm.mediaList && productForm.mediaList.length > 0) {
      setMedia(productForm.mediaList);
    } else if (imagePreview) {
      setMedia([{ file: imagePreview, preview: imagePreview, type: "image" }]);
    } else {
      setMedia([]);
    }
  }, [imagePreview, productForm.mediaList]);

  const handleMediaChange = (newMedia: any[]) => {
    setMedia(newMedia);
    onProductFormChange({ mediaList: newMedia });
    const heroImage = newMedia.find((m) => m.type === "image");
    if (heroImage) {
      onImagePreviewChange(heroImage.preview);
    } else {
      onImagePreviewChange("");
    }
  };

  const countryOptions = React.useMemo(() => {
    if (!Array.isArray(countriesData)) return [];
    const mapped = countriesData
      .map((c: any) => c.countryName || c.name || "")
      .filter(Boolean);
    const uniqueCountries = Array.from(new Set(mapped));
    return uniqueCountries.map((countryName) => ({
      id: countryName,
      value: countryName,
      text: countryName,
    }));
  }, [countriesData]);

  // Storage list logic matching old UI
  const storageList: StorageItem[] =
    productForm.storageList && productForm.storageList.length > 0
      ? productForm.storageList
      : [
          {
            type: productForm.storageType || "PALLETS",
            capacity: productForm.storageCapacity || "",
            minimumQuantity: productForm.storageTrigger || "",
          },
        ];

  const handleStorageChange = (
    index: number,
    field: keyof StorageItem,
    value: string,
  ) => {
    const updated = storageList.map((st, i) =>
      i === index ? { ...st, [field]: value } : st,
    );
    onProductFormChange({
      storageList: updated,
      storageType: updated[0]?.type || "PALLETS",
      storageCapacity: updated[0]?.capacity || "",
      storageTrigger: updated[0]?.minimumQuantity || "",
    });
  };

  const addStorageEntry = () => {
    if (storageList.length >= 2) return;
    const existingTypes = storageList.map((s) => s.type);
    const available = STORAGE_TYPES.find(
      (t) => !existingTypes.includes(t.value),
    );
    const nextType = available ? available.value : "BINS";
    const updated = [
      ...storageList,
      { type: nextType, capacity: "", minimumQuantity: "" },
    ];
    onProductFormChange({ storageList: updated });
  };

  const removeStorageEntry = (index: number) => {
    if (storageList.length <= 1) return;
    const updated = storageList.filter((_, i) => i !== index);
    onProductFormChange({
      storageList: updated,
      storageType: updated[0]?.type || "PALLETS",
      storageCapacity: updated[0]?.capacity || "",
      storageTrigger: updated[0]?.minimumQuantity || "",
    });
  };

  const getAvailableStorageTypes = (currentIndex: number) => {
    const existingTypes = storageList
      .map((st, idx) => (idx !== currentIndex ? st.type : null))
      .filter(Boolean);
    return STORAGE_TYPES.filter((st) => !existingTypes.includes(st.value));
  };

  const handleFeatureToggle = (key: keyof ProductFormState["features"]) => {
    onProductFormChange({
      features: {
        ...productForm.features,
        [key]: !productForm.features[key],
      },
    });
  };

  const handleVariantChange = (
    index: number,
    field: keyof VariantForm,
    value: any,
  ) => {
    const updated = [...formVariants];
    updated[index] = { ...updated[index], [field]: value };
    onVariantsChange(updated);
  };

  const addVariant = () => {
    onVariantsChange([
      ...formVariants,
      { sku: "", name: "", value: "", price: "", image: "" },
    ]);
  };

  const removeVariant = (index: number) => {
    const updated = [...formVariants];
    updated.splice(index, 1);
    onVariantsChange(updated);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="  overflow-hidden">
        {!hideHeaderButtons && (
          <div className="flex justify-between items-center p-2 border-b">
            <div className="flex items-center gap-2">
              {[
                { key: "variants", label: "Variants" },
                { key: "expiry", label: "Expiry" },
                { key: "dangerGoods", label: "Dangerous" },
                { key: "compliance", label: "Compliance" },
                { key: "terms", label: "Terms & Policies" },
              ].map((f) => (
                <label
                  key={f.key}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition cursor-pointer ${
                    productForm.features[
                      f.key as keyof typeof productForm.features
                    ]
                      ? "bg-yellow-50 border-yellow-300"
                      : "bg-white border-slate-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={
                      productForm.features[
                        f.key as keyof typeof productForm.features
                      ]
                    }
                    onChange={() =>
                      handleFeatureToggle(
                        f.key as keyof ProductFormState["features"],
                      )
                    }
                    className="w-4 h-4 accent-yellow-600"
                  />
                  <span className="text-xs font-medium text-[#212121]">
                    {f.label}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <ButtonStyled
                onClick={onDiscard}
                variant="outline"
                value="Cancel"
              />
              <ButtonStyled
                type="submit"
                disabled={isSubmitting}
                isPending={isSubmitting}
                variant="outline"
                value={
                  editingProductId
                    ? "Update Item"
                    : "Add Item"
                }
              />
            </div>
          </div>
        )}

        <div className="p-5 space-y-3">
          {hideHeaderButtons && (
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-3 mb-4 mt-1">
              {[
                { key: "variants", label: "Variants" },
                { key: "expiry", label: "Expiry" },
                { key: "dangerGoods", label: "Dangerous" },
                { key: "compliance", label: "Compliance" },
                { key: "terms", label: "Terms & Policies" },
              ].map((f) => (
                <label
                  key={f.key}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition cursor-pointer ${
                    productForm.features[
                      f.key as keyof typeof productForm.features
                    ]
                      ? "bg-yellow-50 border-yellow-300"
                      : "bg-white border-slate-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={
                      productForm.features[
                        f.key as keyof typeof productForm.features
                      ]
                    }
                    onChange={() =>
                      handleFeatureToggle(
                        f.key as keyof ProductFormState["features"],
                      )
                    }
                    className="w-4 h-4 accent-yellow-600"
                  />
                  <span className="text-xs font-medium text-[#212121]">
                    {f.label}
                  </span>
                </label>
              ))}
            </div>
          )}

          {/* 01. Media */}
          <div className="space-y-2">
            <SectionHeader title=" Media" />

            <MediaUploader
              media={media}
              onMediaChange={handleMediaChange}
              isEditing={true}
              inline={true}
            />
          </div>

          {/* 02. Features */}

          {/* 02. Basic Info */}
          <div className="space-y-2">
            <SectionHeader title="Basic Details" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                name="name"
                title="Product Name"
                value={productForm.name}
                error={errors?.name}
                validation={{ required: true }}
                other={{
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    onProductFormChange({ name: e.target.value }),
                }}
              />
              <Input
                name="sku"
                title="SKU"
                value={productForm.sku}
                error={errors?.sku}
                validation={{ required: true }}
                other={{
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    onProductFormChange({ sku: e.target.value }),
                }}
              />
              <SelectInput
                name="category"
                label="Category"
                placeholder="Select Category"
                value={selectedCategoryValue}
                error={errors?.category}
                validation={{ required: true }}
                selectOptions={
                  Array.isArray(uniqueCategories)
                    ? uniqueCategories.map((c: any, idx: number) => {
                        const val = c.id || c.category || c.categoryName;
                        const label =
                          c.categoryName || c.category || c.name || c.id;
                        return {
                          id: c.id || idx,
                          value: val,
                          text: label,
                        };
                      })
                    : []
                }
                onChange={(val: any) =>
                  onProductFormChange({
                    category: val,
                    subcategory: "",
                  })
                }
              />
              <SelectInput
                name="subcategory"
                label="Subcategory"
                placeholder="Select Subcategory"
                value={selectedSubCategoryValue}
                disabled={!productForm.category}
                selectOptions={
                  Array.isArray(uniqueSubCategories)
                    ? uniqueSubCategories.map((sc: any, idx: number) => {
                        const val = sc.id || sc.subCategory || sc.name;
                        const label =
                          sc.subCategory ||
                          sc.subCategoryName ||
                          sc.name ||
                          sc.id;
                        return {
                          id: sc.id || idx,
                          value: val,
                          text: label,
                        };
                      })
                    : []
                }
                onChange={(val: any) =>
                  onProductFormChange({ subcategory: val })
                }
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                name="price"
                type="number"
                title="Price (SAR)"
                value={productForm.price}
                error={errors?.price}
                validation={{ required: true }}
                other={{
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    onProductFormChange({ price: e.target.value }),
                }}
              />
              <Input
                name="discount"
                type="number"
                title="Discount (SAR)"
                value={productForm.discount}
                other={{
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    onProductFormChange({ discount: e.target.value }),
                }}
              />
              <Input
                name="minOrder"
                type="number"
                title="Min Order"
                value={productForm.minOrder}
                other={{
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    onProductFormChange({ minOrder: e.target.value }),
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                name="shortDesc"
                title="Short Description"
                value={productForm.shortDesc}
                other={{
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    onProductFormChange({ shortDesc: e.target.value }),
                }}
              />
              <Input
                name="longDesc"
                title="Long Description"
                value={productForm.longDesc}
                other={{
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    onProductFormChange({ longDesc: e.target.value }),
                }}
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Weight", unit: "kg", key: "dimensionsWeight", required: true },
                { label: "Length", unit: "cm", key: "dimensionsLength", required: false },
                { label: "Width", unit: "cm", key: "dimensionsWidth", required: false },
                { label: "Height", unit: "cm", key: "dimensionsHeight", required: false },
              ].map((dim) => {
                const hasError = errors?.[dim.key];

                return (
                  <Input
                    key={dim.key}
                    name={dim.key}
                    type="number"
                    title={`${dim.label} (${dim.unit})`}
                    value={(productForm[dim.key as keyof ProductFormState] as string) || ""}
                    error={hasError}
                    validation={dim.required ? { required: true } : undefined}
                    other={{
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        onProductFormChange({ [dim.key]: e.target.value }),
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* 04. Storage & Dimensions */}

          <div className="space-y-0 border rounded border-dashed">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-dashed px-4 py-2">
              <SectionHeader title="Storage" className="w-full" />

              <div className="flex items-center justify-end gap-2 w-full">
                <button
                  type="button"
                  onClick={addStorageEntry}
                  disabled={storageList.length >= 2}
                  className="bg-[#FBD037]/20 border border-[#FBD037] rounded-lg p-1.5 hover:bg-[#FBD037]/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <IconPlus size={16} stroke={1.5} className="text-black" />
                </button>
              </div>
            </div>

            {/* Storage Rows */}
            {storageList.map((st, index) => (
              <div
                key={index}
                className="flex items-start gap-3 px-4 py-2 border-b border-dashed"
              >
                {/* Storage Type */}
                <div className="flex-1">
                  <SelectInput
                    name={`storage_${index}_type`}
                    label="Storage Type"
                    placeholder="Select Storage Type"
                    value={st.type}
                    error={errors?.[`storage_${index}_type`]}
                    validation={{ required: true }}
                    selectOptions={getAvailableStorageTypes(index).map((opt) => ({
                      value: opt.value,
                      text: opt.text,
                    }))}
                    onChange={(val: any) =>
                      handleStorageChange(index, "type", val)
                    }
                  />
                </div>

                {/* Capacity */}
                <div className="flex-1">
                  <Input
                    name={`storage_${index}_capacity`}
                    type="number"
                    title="Capacity"
                    value={st.capacity}
                    error={errors?.[`storage_${index}_capacity`]}
                    validation={{ required: true }}
                    other={{
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleStorageChange(index, "capacity", e.target.value),
                    }}
                  />
                </div>

                {/* Minimum Quantity */}
                <div className="flex-1">
                  <Input
                    name={`storage_${index}_minimumQuantity`}
                    type="number"
                    title="Min. Qty"
                    value={st.minimumQuantity}
                    error={errors?.[`storage_${index}_minimumQuantity`]}
                    validation={{ required: true }}
                    other={{
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleStorageChange(
                          index,
                          "minimumQuantity",
                          e.target.value,
                        ),
                    }}
                  />
                </div>

                {/* Remove */}
                {storageList.length > 1 && (
                  <div className="flex justify-center items-center mt-3">
                    <button
                      type="button"
                      onClick={() => removeStorageEntry(index)}
                      className="bg-[#FBD037]/20 border border-[#FBD037] rounded-lg p-1.5 hover:bg-red-100"
                    >
                      <IconMinus
                        size={16}
                        stroke={1.5}
                        className="text-black"
                      />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {productForm.features.variants && (
            <div className="space-y-0 border rounded border-dashed">
              {/* Header */}
              <div className="flex justify-between items-center border-b border-dashed px-4 py-2">
                <SectionHeader title="Variants" className="w-full" />

                <div className="flex items-center justify-end gap-2 w-full">
                  <button
                    type="button"
                    onClick={addVariant}
                    className="bg-[#FBD037]/20 border border-[#FBD037] rounded-lg p-1.5 hover:bg-[#FBD037]/30"
                  >
                    <IconPlus size={16} stroke={1.5} className="text-black" />
                  </button>
                </div>
              </div>

              {/* Variant Rows */}
              {formVariants.map((v, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-dashed last:border-b-0 items-start"
                >
                  {/* Image */}
                  <div className="col-span-1">
                    {i === 0 ? (
                      <label className="block text-xs text-[#1E293B] mb-1">
                        Image
                      </label>
                    ) : (
                      ""
                    )}

                    <div className="relative w-10 h-10">
                      <input
                        type="file"
                        id={`variant-upload-${i}`}
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];

                          if (file) {
                            handleVariantChange(i, "image", file);
                          }
                        }}
                      />

                      <label
                        htmlFor={`variant-upload-${i}`}
                        className="flex items-center justify-center w-10 h-10 rounded-lg overflow-hidden border-2 border-dashed border-gray-200 bg-[#FFFDF5] hover:border-amber-400 cursor-pointer transition-all"
                      >
                        {v.image ? (
                          <img
                            src={getFormattedImgUrl(v.image)}
                            alt="Variant"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <IconUpload className="w-4 h-4 text-amber-600/60" />
                        )}
                      </label>
                    </div>
                  </div>

                  {/* SKU */}
                  <div className="col-span-3">
                    {i === 0 ? (
                      <label className="block text-xs text-[#1E293B] mb-1">
                        SKU <span className="text-red-500">*</span>
                      </label>
                    ) : (
                      ""
                    )}

                    <input
                      type="text"
                      placeholder="Enter SKU"
                      value={v.sku}
                      onChange={(e) =>
                        handleVariantChange(i, "sku", e.target.value)
                      }
                      className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-xs text-[#212121] focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
                    />
                  </div>

                  {/* Name */}
                  <div className="col-span-3">
                    {i === 0 ? (
                      <label className="block text-xs text-[#1E293B] mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                    ) : (
                      ""
                    )}

                    <input
                      type="text"
                      placeholder="Enter Name"
                      value={v.name}
                      onChange={(e) =>
                        handleVariantChange(i, "name", e.target.value)
                      }
                      className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-xs text-[#212121] focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
                    />
                  </div>

                  {/* Value */}
                  <div className="col-span-2">
                    {i === 0 ? (
                      <label className="block text-xs text-[#1E293B] mb-1">
                        Value <span className="text-red-500">*</span>
                      </label>
                    ) : (
                      ""
                    )}

                    <input
                      type="text"
                      placeholder="Enter Value"
                      value={v.value}
                      onChange={(e) =>
                        handleVariantChange(i, "value", e.target.value)
                      }
                      className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-xs text-[#212121] focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
                    />
                  </div>

                  {/* Price */}
                  <div className="col-span-2">
                    {i === 0 ? (
                      <label className="block text-xs text-[#1E293B] mb-1">
                        Price <span className="text-red-500">*</span>
                      </label>
                    ) : (
                      ""
                    )}

                    <input
                      type="number"
                      placeholder="Enter Price"
                      value={v.price}
                      onChange={(e) =>
                        handleVariantChange(i, "price", e.target.value)
                      }
                      className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-xs text-[#212121] focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
                    />
                  </div>

                  {/* Remove */}
                  <div
                    className={`col-span-1 flex justify-center ${
                      i === 0 ? "items-end mt-6" : "items-center mt-1.5"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => removeVariant(i)}
                      className="bg-[#FBD037]/20 border border-[#FBD037] rounded-lg p-1.5 hover:bg-red-100"
                    >
                      <IconMinus
                        size={16}
                        stroke={1.5}
                        className="text-black"
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {productForm.features.compliance && (
            <div className="space-y-0 border rounded border-dashed">
              {/* Header */}

              <SectionHeader
                title=" Compliance Details"
                className="border-b border-dashed px-4 py-2"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-4 py-2">
                <Input
                  name="complianceCommodity"
                  title="Commodity"
                  value={productForm.complianceCommodity}
                  other={{
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      onProductFormChange({
                        complianceCommodity: e.target.value,
                      }),
                  }}
                />
                <Input
                  name="complianceHsCode"
                  title="HS Code"
                  value={productForm.complianceHsCode}
                  other={{
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      onProductFormChange({
                        complianceHsCode: e.target.value,
                      }),
                  }}
                />
                <SelectInput
                  name="complianceTradeType"
                  label="Trade Type"
                  placeholder="Select Trade Type"
                  value={productForm.complianceTradeType}
                  selectOptions={[
                    { value: "Import", text: "Import" },
                    { value: "Export", text: "Export" },
                  ]}
                  onChange={(val: any) =>
                    onProductFormChange({
                      complianceTradeType: val,
                    })
                  }
                />
                <SelectInput
                  name="complianceOrigin"
                  label="Origin"
                  placeholder="Select Origin"
                  value={productForm.complianceOrigin}
                  selectOptions={countryOptions.map((opt) => ({
                    value: opt.value,
                    text: opt.text,
                  }))}
                  onChange={(val: any) =>
                    onProductFormChange({
                      complianceOrigin: val,
                    })
                  }
                />
                <SelectInput
                  name="complianceDestination"
                  label="Destination"
                  placeholder="Select Destination"
                  value={productForm.complianceDestination}
                  selectOptions={countryOptions.map((opt) => ({
                    value: opt.value,
                    text: opt.text,
                  }))}
                  onChange={(val: any) =>
                    onProductFormChange({
                      complianceDestination: val,
                    })
                  }
                />

                <Input
                  name="complianceDuty"
                  type="number"
                  title="Duty Tax %"
                  value={productForm.complianceDuty}
                  other={{
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      onProductFormChange({ complianceDuty: e.target.value }),
                  }}
                />
                <Input
                  name="complianceVat"
                  type="number"
                  title="VAT %"
                  value={productForm.complianceVat}
                  other={{
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      onProductFormChange({ complianceVat: e.target.value }),
                  }}
                />
                <SelectInput
                  name="complianceRestricted"
                  label="Restricted"
                  placeholder="Select Restricted"
                  value={productForm.complianceRestricted}
                  selectOptions={[
                    { value: "No", text: "No" },
                    { value: "Yes", text: "Yes" },
                  ]}
                  onChange={(val: any) =>
                    onProductFormChange({
                      complianceRestricted: val,
                    })
                  }
                />
                <SelectInput
                  name="complianceLicense"
                  label="License"
                  placeholder="Select License"
                  value={productForm.complianceLicense}
                  selectOptions={[
                    { value: "Not Required", text: "Not Required" },
                    { value: "Required", text: "Required" },
                  ]}
                  onChange={(val: any) =>
                    onProductFormChange({
                      complianceLicense: val,
                    })
                  }
                />
                <div className="md:col-span-3">
                  <Input
                    name="complianceDocuments"
                    title="Documents"
                    value={productForm.complianceDocuments}
                    other={{
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        onProductFormChange({
                          complianceDocuments: e.target.value,
                        }),
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* 06. Variants (With Image Upload section like Old UI) */}

          {/* 07. Terms */}
          {productForm.features.terms && (
            <div className="space-y-2">
              <span className="text-xs font-semibold text-[#212121]/60 uppercase tracking-wider">
                Terms & Policies
              </span>
              <TextareaField
                name="terms"
                label="Terms & Policies"
                placeholder="Warranty, returns, liability..."
                value={productForm.terms}
                showToolbar={true}
                rows={3}
                maxLength={500}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  onProductFormChange({ terms: e.target.value })
                }
              />
            </div>
          )}
        </div>
      </div>
    </form>
  );
};